import {
  ChartConfig,
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from "design-system/molecules/Chart"
import { ChartCard } from "design-system/organisms/insights/ChartCard"
import { capitalize, formatDate } from "lopermut"
import { useNPSQuery } from "modules/insights/hooks/useNPSQuery"
import { useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { Bar, BarChart, TooltipContentProps, XAxis } from "recharts"
import { TimeFrame } from "schema"

type Props = {
  data: ReturnType<typeof useNPSQuery>["data"]
  isPending: boolean
  timeFrame: TimeFrame
}

const MIN_WIDTH_TO_SHOW_WEEK_LABEL = 600

export function NetPromoterDistribution({ data, isPending }: Props) {
  const { t } = useTranslation()
  const noData = !isPending && data?.data.count === 0

  const [showXLabel, setShowXLabel] = useState(false)
  const chartRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleResize = () => {
      setShowXLabel(
        Boolean(
          chartRef.current?.clientWidth &&
          chartRef.current.clientWidth > MIN_WIDTH_TO_SHOW_WEEK_LABEL,
        ),
      )
    }

    handleResize()
    window.addEventListener("resize", handleResize)

    return () => {
      window.removeEventListener("resize", handleResize)
    }
  }, [])

  const chartData = useMemo(
    () =>
      data?.data.distribution?.map((value, index) => {
        if (noData) {
          const detractors = 10 + (index % 3) * 10
          const passives = 20 + (index % 3) * 10
          const promoters = 100 - detractors - passives

          return {
            period: "-",
            promoters,
            passives,
            detractors,
            empty: 0,
          }
        }

        if (!value?.period) return {}

        const total = value.promoters + value.detractors + value.passives
        const promoters = total ? Math.round((value.promoters / total) * 100) : 0
        const passives = total ? Math.round((value.passives / total) * 100) : 0
        const detractors = total ? 100 - promoters - passives : 0
        const empty = total ? 0 : 100

        return {
          period: `${capitalize(formatDate(value.period.from))} - ${capitalize(formatDate(value.period.to))}`,
          axisPeriod: capitalize(formatDate(value.period.from, "MMM d")),
          promoters,
          passives,
          detractors,
          empty,
        }
      }),
    [data, noData],
  )

  const chartConfig = {
    promoters: {
      label: t("insights.nps.distribution.promoters"),
    },
    passives: {
      label: t("insights.nps.distribution.passives"),
    },
    detractors: {
      label: t("insights.nps.distribution.detractors"),
    },
  } satisfies ChartConfig

  const chart = (
    <ChartContainer config={chartConfig} className="mx-auto w-full h-full" ref={chartRef}>
      <BarChart accessibilityLayer data={chartData} maxBarSize={50}>
        <XAxis
          dataKey="period"
          tickLine={false}
          tickMargin={10}
          interval={0}
          height={40}
          axisLine={false}
          hide={!showXLabel}
          tickFormatter={(_, index) => chartData?.[index]?.axisPeriod ?? ""}
        />
        <ChartTooltip
          content={(props: TooltipContentProps) => {
            const filteredPayload = props.payload
              ?.filter((item) => item.dataKey !== "empty")
              .map((item) => ({
                ...item,
                value: typeof item.value === "number" ? `${item.value}%` : item.value,
                payload: {
                  ...item.payload,
                  [String(item.dataKey)]:
                    typeof item.value === "number" ? `${item.value}%` : item.value,
                },
              }))

            return <ChartTooltipContent {...props} payload={filteredPayload} />
          }}
        />
        <ChartLegend
          content={(props) => {
            const filteredPayload = props.payload?.filter((item) => item.dataKey !== "empty")
            return (
              <ChartLegendContent payload={filteredPayload} verticalAlign={props.verticalAlign} />
            )
          }}
        />
        <Bar dataKey="detractors" stackId="a" fill="var(--negative)" />
        <Bar dataKey="passives" stackId="a" fill="var(--neutral)" />
        <Bar dataKey="promoters" stackId="a" fill="var(--positive)" />
        <Bar dataKey="empty" stackId="a" fill="var(--muted)" />
      </BarChart>
    </ChartContainer>
  )

  return (
    <ChartCard
      title={t("insights.nps.distribution.title")}
      description={t("insights.nps.distribution.description")}
      chart={chart}
      isPending={isPending}
      noData={{
        show: noData,
        title: t("insights.nps.noData.title"),
        description: t("insights.nps.noData.description"),
      }}
    />
  )
}
